import * as Pickr from '@simonwep/pickr/dist/pickr.es5.min'; //support old navigator version
import { SVG, Svg } from '@svgdotjs/svg.js';
import { delegate, q, ww } from 'bobjoll/ts/library/dom';
import { EventTracker } from 'Library/eventTacker';
import * as GPickr from 'Library/gpickr/gpickr.min.js';
import getXHR from 'Partials/xhr';
import { Color } from 'Pods/editor/color';
import { Shape } from 'Pods/editor/shape';
import { EditorHistory } from 'Pods/editor/history';
import { EditorHelpers } from 'Pods/editor/editor.helpers';
import { ColorPalette } from 'Pods/editor/colorPalette';
import { handleEventTransformations } from 'Pods/editor/transformation';
import { LazyModal } from 'Pods/modals/lazyModal';
import { Downloads } from 'Project/ts/common-app/downloads';
import { FeatureFlag } from 'Project/ts/library/featureFlag';
import { Attribution } from 'Project/ts/partials/attributionService';
import User from 'Project/ts/pods/user/main';

export class Editor {
    public static formElement: HTMLFormElement;
    public static pickr: any;
    public static gpickr: any;
    public static draw: Svg;
    public static initial: Svg;
    public static history: EditorHistory;
    public static iconData: IconData;
    public static gradientColorsArray: any = [];
    public static plainColor: string;
    private static downloadElements: NodeList = document.querySelectorAll(
        '.detail__editor .popover--download a',
    ) as NodeList;
    public static editorTrackerTag: string;
    public static editForRegisterTrackerTag: string;
    private static MAX_ICON_PER_COLLECTION: number = 256;
    private static MAX_EDITED_ICON_PER_COLLECTION: number = 3;

    public static init(inModalView: boolean, open: boolean = false) {
        ww.Pickr = Pickr;
        ww.GPickr = GPickr;
        this.close();
        this.handleGATrackerEvents();
        this.handleListeners();

        ColorPalette.init(User.isPremium());

        this.formElement = document.querySelector('#download-form') as HTMLFormElement;

        if (ww.view.section == 'icon-detail') {
            this.formElement = document.querySelector('#detail #download-form') as HTMLFormElement;
        }

        this.handleModalView(inModalView);

        if (open) {
            this.open();
        }
        if (this.iconData.hasRaw) {
            import('Pods/editor/stroke').then(module => {
                ww.Stroke = module.Stroke;
                // ww.Stroke.init();
            });
        }

        this.removeFocusDisplayButton();
        this.removeFocusDownloadButton();
        this.handleDownloadButtonsEvents();
    }

    private static getBase64Resource(svgString: string, format: string, size: string) {
        const encoded: string = btoa(svgString);
        const imgURI: string = 'data:image/svg+xml;base64,' + encoded;

        Downloads.fillBase64Form(imgURI);

        this.trackDownload(format, size);
    }

    private static getResourceByFormat(format: string, size: string) {
        const svgString: string = document.querySelector('.detail__editor__icon-holder')?.innerHTML || '';

        switch (format) {
            case 'base64':
                this.getBase64Resource(svgString, format, size);
                break;
            case 'svg':
            case 'eps':
            case 'png':
            case 'jpeg':
            case 'raw-svg':
                this.serverDownload(svgString, format, size);
                break;
            default:
                return;
        }

        const dataToSend = {
            event: 'ga4event',
            event_name: 'file_download',
            category: ww.Resource.resource.type === 'standard' ? 'icons' : `${ww.Resource.resource.type}s`,
            location: 'editor',
            label: ww.Resource.resource.pack.name,
            file_id: ww.Resource.resource.id,
            file_name: ww.Resource.resource.name,
            author_name: ww.Resource.resource.author.name,
            file_extension: format,
            file_size: size,
            action: 'download',
        };

        if (dataToSend.category === 'uicons') {
            dataToSend.category = 'interface_icons';
            dataToSend.author_name = 'Uicons';
        }

        if (dataToSend.category === 'animated-icons') {
            dataToSend.category = 'animated_icons';
        }

        ww.EventTracker.send('ga4dl', dataToSend);
    }

    private static downloadEventCallback(e: Event) {
        e.preventDefault();
        e.stopPropagation();

        if (ww.Essentials) {
            ww.Essentials.handleDownloadClick();
        } else {
            const currentElement = e.currentTarget as HTMLElement;
            const format = currentElement.getAttribute('data-format') || '';
            const size = currentElement.dataset.size || '';
            const iconData = (document.querySelector('#detail') as HTMLElement).dataset;
            const iconId = iconData.elementid || Editor.iconData?.id;

            if (User.isPremiumFlaticon()) {
                return Editor.finalDownload(currentElement as HTMLElement);
            } else if (!User.isPremium() && format != 'png') {
                LazyModal.callModalDownloadOnlyForPremium(iconId);
                return;
            } else {
                if (iconData.icon_type === 'uicon') {
                    return Editor.finalDownload(currentElement as HTMLElement);
                }

                import('Pods/editor/attributionEditor').then(module =>
                    module.AttributionEditor.setUpModalFreeDownload(),
                );
                let buttonFreeDownload = q('#download-free') as HTMLButtonElement;
                buttonFreeDownload.setAttribute('data-format', format || '');
                buttonFreeDownload.setAttribute('data-size', size || '');
                (document.getElementById('modal-download') as HTMLInputElement).checked = true;
            }
        }
    }

    public static finalDownload = (element: HTMLElement) => {
        let svgElement = document.querySelector('.detail__editor__icon-holder svg');

        (document.querySelector(
            '.detail__editor .popover-container .popover-content',
        ) as HTMLDivElement)?.classList.toggle('show-menu');
        (document.querySelector(
            '.detail__editor .popover-container #download_button',
        ) as HTMLDivElement)?.classList.remove('active');
        let editorSizesMenu = document.querySelector('.detail__editor__sizes-toggle') as HTMLInputElement;

        if (editorSizesMenu) {
            editorSizesMenu.checked = false;
        }

        if (svgElement) {
            const size = element.getAttribute('data-size') ? element.getAttribute('data-size') : '512';
            const format = element.getAttribute('data-format') || '';

            if (size) {
                svgElement.setAttribute('width', size);
                svgElement.setAttribute('height', size);
            }

            if (element.classList.contains('png_dropper')) {
                element?.parentNode?.parentNode?.querySelector('.customize')?.classList.add('active');
                return;
            }

            Editor.getResourceByFormat(format, size || '512');

            svgElement.setAttribute('width', '512');
            svgElement.setAttribute('height', '512');
        }
    };

    private static handleDownloadButtonsEvents() {
        this.downloadElements.forEach(buttonElement => {
            buttonElement.removeEventListener('click', this.downloadEventCallback, true);

            buttonElement.addEventListener('click', this.downloadEventCallback, true);
        });
    }

    public static open() {
        document.body.classList.add('overflow--mobile');

        let detailElement = document.querySelector('#detail') as HTMLElement;
        if (detailElement) {
            let editorElement = document.querySelector('.detail__editor');
            let detailIconElement = detailElement.querySelector('.detail__top');
            if (editorElement && detailIconElement) {
                detailIconElement.classList.add('hide');
                editorElement.classList.remove('hide');
                document
                    .querySelector('#detail .detail__inner')
                    ?.insertBefore(editorElement, document.querySelector('.detail__top'));
            }

            let formElement;
            if (ww.view.section == 'icon-detail') {
                formElement = document.querySelector('#detail #download-form');
            }

            if (ww.view.section == 'uicons-detail-page') {
                formElement = document.querySelector('#detail #download-edited-uicon-form');
            }

            if (formElement) {
                this.iconData = {
                    id: ww.Resource.resource.id,
                    name: ww.Resource.resource.name,
                    author: ww.Resource.resource.author,
                    color: ww.Resource.resource.color,
                    premium: ww.Resource.resource.premium,
                    isColor: ww.Resource.resource.color == '2' ? true : false,
                    resourceType: ww.Resource.resource.type,
                    hasRaw: ww.Resource.resource.hasRaw,
                };

                let icon = document.querySelector(
                    `ul:not(.collection_icons_list) li.icon--item[data-id='${this.iconData.id}']`,
                ) as HTMLElement;

                if (icon == null) {
                    icon = document.querySelector('.detail__icon__inner img') as HTMLElement;
                }

                document.querySelector('.only_registered .alert_no_user')?.classList.remove('zoomOut');
                if (gr.auth.logged) {
                    document.querySelector('.only_registered')?.classList.add('hidden');
                    const downloadButtonElement = editorElement?.querySelector('#download_button') as HTMLButtonElement;
                    if (downloadButtonElement) {
                        downloadButtonElement.disabled = false;
                    }
                } else {
                    document.querySelector('.only_registered')?.classList.remove('hidden');
                    document.querySelector('.only_registered .alert_no_user')?.classList.remove('hidden');
                }

                if (User.isLogged()) {
                    this.iconData.imageUrl = icon.dataset.icon_src
                        ? icon.dataset.icon_src
                        : (icon as HTMLImageElement).src;
                    Editor.getSvgResource();
                }
                ww.IconData = this.iconData;
            }

            if (this.iconData.hasRaw) {
                document.getElementById('option_edit__stroke')?.classList.remove('hidden');
            }
        }

        let gradientElements = document
            .querySelector('.detail__editor__icon-wrapper svg')
            ?.querySelectorAll('linearGradient') as NodeList;
        if (!gradientElements || (gradientElements && gradientElements.length == 0)) {
            this.history = new EditorHistory();
        }

        this.editorTrackerTag = 'editor';
        this.editForRegisterTrackerTag = 'edit-icon';

        const resourceType = this.iconData.resourceType;
        if (['sticker', 'uicon'].includes(this.iconData.resourceType)) {
            this.editorTrackerTag = `editor-${resourceType}'`;
            this.editForRegisterTrackerTag = `edit-${resourceType}`;
            document.querySelector('#save_into_active')?.classList.add('hidden');
        }

        EventTracker.send('ga', 'event', this.editorTrackerTag, 'open');
    }

    public static close() {
        document.body.classList.remove('overflow--mobile');

        let detailElement = document.querySelector('#detail') as HTMLElement;
        if (detailElement) {
            let editorElement = document.querySelector('.detail__editor') as HTMLElement;
            let detailIconElement = detailElement.querySelector('.detail__top');
            if (editorElement && detailIconElement) {
                editorElement.classList.add('hide');
                detailIconElement.classList.remove('hide');
            }
            document.body.appendChild(editorElement);
        }

        let editorContainer = document.querySelector('#icon-edit-color-picker');

        if (editorContainer) {
            editorContainer.innerHTML = '<div class="color-picker-wrapper"></div>';
        }
        this.history?.reset();
        let submenuInputColor = document.querySelector(
            '.detail__editor__submenu input[id="editor__colors"]',
        ) as HTMLInputElement;
        if (submenuInputColor) {
            submenuInputColor.checked = true;
        }
        document
            .querySelectorAll('.detail__editor__submenu input[name="editor__option"]:not([id="editor__colors"])')
            .forEach((inputSubmenuElement: HTMLInputElement) => {
                inputSubmenuElement.checked = false;
            });
        document.querySelector(`.detail__editor__menu li label[for="editor__colors"]`)?.classList.add('active');
        document
            .querySelectorAll(`.detail__editor__menu li label:not([for="editor__colors"])`)
            .forEach(labelElement => {
                labelElement.classList.remove('active');
            });
        const editorElement = document.querySelector('.detail__editor') as HTMLElement;
        const resetIconBtn = editorElement?.querySelector('#_svg_editor_resetBtn') as HTMLButtonElement;
        resetIconBtn.addEventListener('click', Editor.getSvgResource);
        Editor.cleanSvgHolder();
        Editor.cleanAlertsAndButtons();
    }

    public static handleListeners() {
        let buttonCloseEditElement = document.querySelector('.detail__editor button.close');
        buttonCloseEditElement?.removeEventListener('click', this.close);
        buttonCloseEditElement?.addEventListener('click', this.close);

        let detailMenuElements = document.querySelectorAll('.detail__editor__menu li');
        detailMenuElements.forEach(element => {
            element?.addEventListener('click', e => {
                let forElement = (e.currentTarget as HTMLElement)?.querySelector('label')?.htmlFor;
                if (forElement == 'editor__forms') {
                    document.querySelectorAll('.detail__editor__controls span button').forEach(element => {
                        element.classList.add('hide');
                    });
                } else {
                    document.querySelectorAll('.detail__editor__controls span button').forEach(element => {
                        element.classList.remove('hide');
                    });
                }
                document.querySelector(`.detail__editor__menu li label[for="${forElement}"]`)?.classList.add('active');
                document
                    .querySelectorAll(`.detail__editor__menu li label:not([for="${forElement}"])`)
                    .forEach(labelElement => {
                        labelElement.classList.remove('active');
                    });
            });
        });

        delegate('#save_into_active', 'click', function(this: HTMLButtonElement, e: Event) {
            if (this.disabled == false) {
                Editor.saveIntoCollection(e);
            }
        });
    }

    public static handleEditorListeners() {
        const editorElement = document.querySelector('.detail__editor') as HTMLElement;
        const pcrApp = editorElement?.querySelector('.pcr-app') as HTMLDivElement;
        const editorPaletteReplaceElement = editorElement?.querySelector('.color-tab-palette-replace') as HTMLElement;
        const historyElement = editorPaletteReplaceElement?.querySelector('p:nth-of-type(3)') as HTMLElement;
        const editorControlsElement = editorElement?.querySelector('.detail__editor__controls') as HTMLElement;
        const buttonsEditorControls = editorControlsElement?.querySelectorAll('.tooltip.tooltip--top');
        const listHistoryElement = editorElement?.querySelector('#last-icon-colors') as HTMLButtonElement;
        const saveCollectionBtn = editorElement?.querySelector('#save_into_active') as HTMLButtonElement;
        let gradientElements = document
            .querySelector('.detail__editor__icon-wrapper svg')
            ?.querySelectorAll('linearGradient') as NodeList;

        pcrApp?.classList.add('hidden');

        if (gradientElements && gradientElements.length > 0) {
            historyElement?.classList.add('hidden');
            listHistoryElement?.classList.add('hidden');
            buttonsEditorControls?.forEach(button => {
                button.classList.add('hidden');
            });
        } else {
            historyElement?.classList.remove('hidden');
            listHistoryElement?.classList.remove('hidden');
            buttonsEditorControls?.forEach(button => {
                button.classList.remove('hidden');
            });
        }

        if (this.pickr) {
            document
                .querySelectorAll('#icon-edit-color-picker .pcr-selection > div[class*="pcr-color-"]')
                .forEach((item: Element) => {
                    item.addEventListener('mousedown', () => {
                        const editorInputElement = document.querySelector(
                            '.detail__editor .pcr-interaction .pcr-result',
                        ) as HTMLElement;
                        editorInputElement.blur();
                    });
                });

            this.pickr.on('changestop', (_source: string, instance: any) => {
                let color = instance
                    .getColor()
                    .toHEXA()
                    .toString()
                    .toLowerCase();
                Color.fireChangeColor(color);
            });

            this.pickr.on('change', (color: any) => {
                saveCollectionBtn.disabled = false;
                this.setAlertAndDownloadButton(false);

                Color.setSVGColor(
                    color
                        .toHEXA()
                        .toString()
                        .toLowerCase(),
                );

                const editorInputElement = document.querySelector(
                    '.detail__editor .pcr-interaction .pcr-result',
                ) as HTMLFormElement;
                if (
                    editorInputElement &&
                    document.activeElement === editorInputElement &&
                    editorInputElement.value.length == 7
                ) {
                    Color.fireChangeColor(
                        color
                            .toHEXA()
                            .toString()
                            .toLowerCase(),
                    );
                }
            });

            this.pickr.on('show', function() {
                pcrApp?.classList.remove('hidden');
            });

            this.pickr.on('hide', function() {
                pcrApp?.classList.add('hidden');
            });

            q('.pcr-result')?.setAttribute('maxlength', '7');
        }

        if (this.gpickr) {
            const resetIconBtn = editorElement?.querySelector('#_svg_editor_resetBtn') as HTMLButtonElement;
            const gpcrInteraction = editorElement?.querySelector('.gpcr-interaction') as HTMLDivElement;

            this.gpickr.on('change', () => {
                let stopsGradientArray = this.gpickr.getStops();
                let buttonElement = document.querySelector('#svg-icon-colors button.active') as HTMLElement;
                let currentGradientColor = this.gpickr.getGradient();
                if (buttonElement) {
                    let svgId = buttonElement.getAttribute('data-original');

                    if (stopsGradientArray.length > 1) {
                        Color.setSVGGradientColor(stopsGradientArray, `[id = ${svgId}]`);
                    }

                    buttonElement.style.background = currentGradientColor;
                }
                saveCollectionBtn.disabled = false;
                resetIconBtn.disabled = false;
                this.setAlertAndDownloadButton(false);
            });

            if (this.iconData.id) {
                resetIconBtn.addEventListener('click', this.getSvgResource, { once: true });
            }

            this.gpickr._pickr.on('show', function() {
                pcrApp?.classList.remove('hidden');
                gpcrInteraction?.classList.remove('hidden');
            });

            this.gpickr._pickr.on('hide', function() {
                pcrApp?.classList.add('hidden');
                gpcrInteraction?.classList.add('hidden');
            });
        }
    }

    private static getSvgResource = () => {
        const url = `${APP_URL}editor/icon/svg/${Editor.iconData.id}?type=${Editor.iconData.resourceType}`;
        getXHR(url, {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then(async response => {
            if (response.url) {
                Editor.loadSVG(response.url);
            }
        });
        if (document.querySelector('.gpcr-stop-marker')) {
            let stopMakerArray: any = document.querySelector('.gpcr-stop-marker')?.children;

            Array.from(stopMakerArray).forEach((element: any) => {
                element.remove();
            });

            Editor.addStopGradient();
        }
    };

    public static handleModalView(inModalView: boolean) {
        let editorElement = document.querySelector('.detail__editor');
        if (inModalView && editorElement) {
            document
                .querySelector('#detail .detail__inner')
                ?.insertBefore(editorElement, document.querySelector('.detail__top'));
        } else if (editorElement) {
            if (document.querySelector('#detail .detail__inner') && !document.getElementById('detail-overlay')) {
                document
                    .querySelector('#detail .detail__inner')
                    ?.insertBefore(editorElement, document.querySelector('.detail__top'));
            } else {
                document.querySelector('body')?.appendChild(editorElement);
            }
        }
    }

    private static pickrInit() {
        this.pickr = ww.Pickr.create({
            el: '.color-picker-wrapper',
            container: '#icon-edit-color-picker',
            showAlways: false,
            inline: false,
            comparison: false,
            useAsButton: true,
            defaultRepresentation: 'HEX',
            default: this.plainColor,
            components: {
                preview: false,
                opacity: true,
                hue: true,
                interaction: {
                    hex: false,
                    rgba: false,
                    hsla: false,
                    hsva: false,
                    cmyk: false,
                    input: true,
                    clear: false,
                    save: false,
                },
            },
        });

        document
            .querySelector('#icon-edit-color-picker .pcr-color-opacity')
            ?.setAttribute('data-gtm-event', 'editor-transparency');

        const editorElement = document.querySelector('.detail__editor') as HTMLElement;
        const colorPickerButton = editorElement?.querySelector('.color-picker-btn');
        colorPickerButton?.classList.add('hidden');
    }

    private static GPickrOpen(e: MouseEvent) {
        Editor.gpickr._pickr.show();
        e.stopPropagation();
        const body = document.querySelector('body');
        body?.removeEventListener('click', Editor.GPickrCloseWhenClickOut);
        body?.addEventListener('click', Editor.GPickrCloseWhenClickOut);
    }

    private static GPickrCloseWhenClickOut(e: MouseEvent) {
        const pcrApp = document.querySelector('.detail__editor .pcr-app') as HTMLDivElement;
        const isClickOnEditor = Boolean((e.target as HTMLElement).closest('#icon-edit-color-picker'));
        const isEditorVisible = pcrApp.classList.contains('visible');
        if (isEditorVisible && !isClickOnEditor) {
            Editor.gpickr._pickr.hide();
            document.querySelector('body')?.removeEventListener('click', Editor.GPickrCloseWhenClickOut);
        }
    }

    private static GPickrInit() {
        const editorElement = document.querySelector('.detail__editor') as HTMLElement;

        const colorPickerButton: HTMLButtonElement | null = editorElement?.querySelector('.color-picker-btn');

        this.gpickr = new ww.GPickr({
            el: '.color-picker-wrapper',
            container: '#icon-edit-color-picker',
            showAlways: false,
            inline: false,
            comparison: false,
            useAsButton: true,
            defaultRepresentation: 'HEX',
            stops: this.gradientColorsArray,
        });

        const gpcrInteraction = editorElement?.querySelector('.gpcr-interaction') as HTMLElement;

        gpcrInteraction?.classList.add('hidden');
        colorPickerButton?.classList.remove('hidden');

        colorPickerButton?.removeEventListener('click', Editor.GPickrOpen);
        colorPickerButton?.addEventListener('click', Editor.GPickrOpen);
    }

    public static addStopGradient() {
        if (this.gradientColorsArray.length > 0) {
            this.gradientColorsArray.forEach((rgbGradientColor: any) => {
                this.gpickr.off().addStop(rgbGradientColor[0], parseFloat(rgbGradientColor[1]));
            });
        }
    }

    private static loadSVG(url: string) {
        EditorHelpers.getSVGCode(url).then((svg: SvgInHtml) => {
            let svgHolder = document.querySelector('.detail__editor__icon-holder');

            if (!svgHolder) {
                return;
            }

            svgHolder.innerHTML = '';
            let viewBoxValues: string[] = [];

            if (svg.getAttribute('viewBox')) {
                viewBoxValues = svg?.getAttribute('viewBox')?.split(' ') || [];
            }

            this.draw = SVG()
                .addTo('.detail__editor__icon-holder')
                .size(512, 512);

            this.draw.attr('x', 0);
            this.draw.attr('y', 0);
            this.draw.attr(
                'viewBox',
                svg.getAttribute('viewBox')
                    ? `0 0 ${viewBoxValues ? viewBoxValues[2] : 0} ${viewBoxValues ? viewBoxValues[3] : 0}`
                    : '0 0 512 512',
            );
            this.draw.attr('style', 'enable-background:new 0 0 512 512');
            this.draw.attr('xml:space', 'preserve');

            const fillRule = svg.getAttribute('fill-rule');
            if (fillRule === 'evenodd') {
                this.draw.attr('fill-rule', 'evenodd');
            }

            this.draw.svg('<g>' + svg.innerHTML + '</g>');
            this.initial = this.draw.clone();
            if (this.draw.node.hasAttribute('xmlns:svgjs')) {
                this.draw.node.removeAttribute('xmlns:svgjs');
            }
            Color.getColors(this.draw);

            if (this.iconData.hasRaw) {
                ww.Stroke.handleEventStroke();
            } else {
                const editStrokeOption = document.getElementById('option_edit__stroke');
                editStrokeOption?.classList.add('hidden');
            }

            this.setAlertAndDownloadButton(this.iconData.color == '2');

            const buttonColorElements = document.querySelectorAll<HTMLButtonElement>('#svg-icon-colors button');

            buttonColorElements.forEach((button: HTMLButtonElement) => {
                button.addEventListener('click', () => Editor.selectColorButton(button), { passive: true });
            });

            this.selectColorButton(buttonColorElements[0]);
        });
    }

    private static selectColorButton(button: HTMLButtonElement) {
        const previousButtonElement = document.querySelector<HTMLElement>('#svg-icon-colors button.active');
        previousButtonElement?.classList.remove('active');

        let buttonCurrentColor = button.getAttribute('data-actual');
        if (buttonCurrentColor) {
            this.plainColor = buttonCurrentColor;
        }
        button.classList.add('active');
        if (document.querySelector('#icon-edit-color-picker')) {
            const editColorPickerElement = document.querySelector<HTMLElement>('#icon-edit-color-picker');
            if (editColorPickerElement) {
                editColorPickerElement.innerHTML = `<div class="color-picker-wrapper"></div>`;
            }
        }
        if (buttonCurrentColor?.includes('#')) {
            this.pickrInit();
        } else {
            Color.updateColorSvg(button);
            this.GPickrInit();
        }
        handleEventTransformations();
        Shape.handleEventShapes();
        if (this.iconData.hasRaw) {
            ww.Stroke.handleEventStroke();
        }
        this.handleEditorListeners();
    }

    private static trackDownload(format: string, size: string) {
        let formData = new FormData();
        let tagFormat = format;

        if (this.iconData.hasRaw && format === 'svg') {
            tagFormat = 'svg editable';
        }

        EventTracker.send('ga', 'event', this.editorTrackerTag, 'download', tagFormat.toUpperCase());

        Downloads.trackDownload(this.iconData.id, format);

        const team = (document.querySelector('#download-form #team') as HTMLInputElement).value;
        formData.append('team', team);

        formData.append('id', this.iconData.id);
        formData.append('format', format);
        formData.append('size', size);
        formData.append('color', '#' + this.iconData.color);

        const request = new XMLHttpRequest();
        request.open('POST', '/downloads/track-download');
        request.send(formData);
    }

    private static serverDownload(svg: string, format: string, size: string) {
        const svgCode = svg;
        let tagFormat = format;

        if (!this.iconData) {
            this.iconData = { ...ww.IconData };
        }

        let form = document.createElement('form');
        form.method = 'POST';
        form.action = FLATICON_DOWNLOAD_URL + 'download/icon/edited';
        form.style.display = 'none';
        for (var k in this.iconData) {
            form.insertAdjacentHTML(
                'beforeend',
                // @ts-ignore
                '<input type="text" name="{0}" value="{1}">'.format(k, this.iconData[k]),
            );
        }

        form.insertAdjacentHTML('beforeend', '<input type="text" name="format" value="' + format + '">');
        form.insertAdjacentHTML('beforeend', '<input type="text" name="size" value="' + size + '">');
        form.insertAdjacentHTML(
            'beforeend',
            '<input type="text" name="code" value="' + btoa(unescape(encodeURIComponent(svgCode))) + '">',
        );
        form.insertAdjacentHTML(
            'beforeend',
            '<input type="text" name="type" value="' + this.iconData.resourceType + '">',
        );
        document.body.appendChild(form);
        form.submit();

        if (this.iconData.hasRaw && format === 'svg') {
            tagFormat = 'svg editable';
        }

        EventTracker.send('ga', 'event', this.editorTrackerTag, 'download', tagFormat.toUpperCase());
        form.parentNode?.removeChild(form);

        if (!User.isPremiumFlaticon()) {
            const { resourceType } = Editor.iconData;
            if (resourceType === 'uicon') {
                return;
            }

            Attribution.initialize('icon');
            Attribution.show();
            (document.getElementById('modal-download') as HTMLInputElement).checked = false;
        } else {
            Downloads.trackDownload(this.iconData.id, format);
        }
    }

    public static setAlertAndDownloadButton(isColor: boolean) {
        let saveIntoButtonElement = document.getElementById('save_into_active') as HTMLButtonElement;

        if (this.iconData.resourceType !== 'sticker') {
            if (User.isLogged()) {
                if (ww.Collection.getActive().status && saveIntoButtonElement) {
                    if (Editor.hasMoreThan3EditedIconPerCollection() && !User.isPremium()) {
                        document.querySelector('.detail__editor .free_user_warning')?.classList.remove('hidden');
                        saveIntoButtonElement.disabled = true;
                    } else if (Editor.hasReachedLimitIconPerCollection()) {
                        document.querySelector('.detail__editor .collection_limit')?.classList.remove('hidden');
                        saveIntoButtonElement.disabled = true;
                    } else {
                        document.querySelector('.detail__editor .free_user_warning')?.classList.add('hidden');
                    }
                } else {
                    saveIntoButtonElement.disabled = true;
                    document.querySelector('.detail__editor .collection_locked')?.classList.remove('hidden');
                }
            } else {
                document.querySelector('.only_registered')?.classList.remove('hidden');
                document
                    .querySelector('.detail__editor .edit-icons-user-actions #download_button')
                    ?.classList.add('hidden');
                saveIntoButtonElement.classList.add('hidden');
            }
        }
    }

    private static saveIntoCollection(e: Event) {
        e.preventDefault();
        e.stopPropagation();

        this.save(false);
    }

    private static save(overwrite: boolean) {
        let svg = document.querySelector('.detail__editor__icon-holder')?.innerHTML;
        let order = ww.db.rowCount(ww.Collection.getActive().bookmark_id) + 1;

        ww.Collection.upload_icon(
            svg,
            this.iconData.isColor,
            order,
            this.iconData.id,
            this.iconData.color,
            this.iconData.name,
            overwrite,
        );

        (document.getElementById('save_into_active') as HTMLButtonElement).disabled = true;
        setTimeout(() => {
            this.setAlertAndDownloadButton(this.iconData.color == '2');
        }, 500);
    }

    private static removeFocusDisplayButton() {
        const editorDisplayElement = document.querySelector(
            '.detail__editor__options.detail__editor__display',
        ) as HTMLElement;
        let editorDisplayButtonsObj = editorDisplayElement.getElementsByTagName('button');

        if (editorDisplayElement) {
            Array.prototype.forEach.call(editorDisplayButtonsObj, function(button: HTMLButtonElement) {
                button.addEventListener('click', function() {
                    button.blur();
                });
            });
        }
    }

    private static removeFocusDownloadButton() {
        const body = document.querySelector('body') as HTMLBodyElement;
        const editorPopoverContent = document.querySelector(
            '.detail__editor .popover-container .popover-content',
        ) as HTMLDivElement;
        const editorDownloadButton = document.querySelector(
            '.detail__editor .popover-container #download_button',
        ) as HTMLDivElement;

        if (editorDownloadButton) {
            editorDownloadButton.addEventListener(
                'click',
                function _listener(event) {
                    event.preventDefault();
                    this.classList.toggle('active');
                    editorPopoverContent.classList.toggle('show-menu');
                    event.stopPropagation();
                    editorDownloadButton.removeEventListener('click', _listener, true);
                },
                true,
            );
        }

        editorPopoverContent.addEventListener(
            'click',
            function(event) {
                event.stopPropagation();
            },
            false,
        );

        body.addEventListener(
            'click',
            function() {
                if (editorPopoverContent && editorPopoverContent.classList.contains('show-menu')) {
                    editorPopoverContent.classList.remove('show-menu');
                    editorDownloadButton.classList.remove('active');
                }
            },
            false,
        );
    }

    private static handlerTracker = (option: string, subOption: string) =>
        EventTracker.send('ga', 'event', Editor.editorTrackerTag, option, subOption);

    private static shapeTracker = (e: MouseEvent) => {
        const buttonClicked = e.currentTarget as HTMLButtonElement;
        let shapeId = buttonClicked.dataset.values;

        if (shapeId === 'rounded') {
            shapeId = 'square-rounded';
        }

        EventTracker.send('ga', 'event', Editor.editorTrackerTag, 'forms', shapeId);
    };

    private static collectionLockedTracker = () =>
        EventTracker.send('ga', 'event', Editor.editorTrackerTag, 'alert-premium');

    private static registerLinkTracker = () =>
        EventTracker.send('ga', 'event', 'register', 'click', Editor.editForRegisterTrackerTag);

    private static handleGATrackerEvents = () => {
        const trackerTagsOptions = {
            menu: {
                name: 'menu',
                options: {
                    color: 'colors',
                    display: 'display',
                    forms: 'forms',
                },
            },
            position: {
                name: 'position',
                options: {
                    move: 'move',
                    rotateFlip: 'rotate-flip',
                },
            },
        };

        (document.querySelector('#editor__colors') as HTMLElement).onclick = () =>
            Editor.handlerTracker(trackerTagsOptions.menu.name, trackerTagsOptions.menu.options.color);
        (document.querySelector('#editor__display') as HTMLElement).onclick = () =>
            Editor.handlerTracker(trackerTagsOptions.menu.name, trackerTagsOptions.menu.options.display);
        (document.querySelector('#editor__forms') as HTMLElement).onclick = () =>
            Editor.handlerTracker(trackerTagsOptions.menu.name, trackerTagsOptions.menu.options.forms);

        (document.querySelectorAll('.edit__move button') as NodeListOf<HTMLElement>).forEach(
            (element: HTMLElement) =>
                (element.onclick = () =>
                    Editor.handlerTracker(trackerTagsOptions.position.name, trackerTagsOptions.position.options.move)),
        );

        (document.querySelectorAll('.edit__rotate button , .edit__flip button') as NodeListOf<HTMLElement>).forEach(
            (element: HTMLElement) =>
                (element.onclick = () =>
                    Editor.handlerTracker(
                        trackerTagsOptions.position.name,
                        trackerTagsOptions.position.options.rotateFlip,
                    )),
        );
        (document.querySelectorAll('.detail__editor__forms button') as NodeListOf<HTMLElement>).forEach(
            (element: HTMLElement) => (element.onclick = Editor.shapeTracker),
        );
        (document.querySelector('.collection_locked a') as HTMLElement).onclick = Editor.collectionLockedTracker;
        (document.querySelector('.edit_register_link') as HTMLElement).onclick = Editor.registerLinkTracker;
    };

    private static cleanSvgHolder = () =>
        ((document.querySelector('.detail__editor__icon-holder') as HTMLElement).innerHTML = '');

    private static cleanAlertsAndButtons = () => {
        document.querySelector('.detail__editor .collection_locked')?.classList.add('hidden');
        document.querySelector('.detail__editor .free_user_warning')?.classList.add('hidden');
        document.querySelector('.detail__editor .collection_limit')?.classList.add('hidden');
    };

    private static hasMoreThan3EditedIconPerCollection = () =>
        ww.db.queryAll(ww.Collection.getActive().bookmark_id, { query: { premium: 4 } }).length >=
        Editor.MAX_EDITED_ICON_PER_COLLECTION;

    private static hasReachedLimitIconPerCollection = () =>
        ww.db.queryAll(ww.Collection.getActive().bookmark_id).length >= Editor.MAX_ICON_PER_COLLECTION;
}

type SvgInHtml = HTMLElement & SVGElement;
interface IconData {
    id: string;
    name: string;
    author: string;
    color: string;
    premium: string;
    imageUrl?: string;
    isColor: boolean;
    resourceType: string;
    hasRaw?: boolean | string;
}
